/**
 * Phase 5 PR 5.1 — `useMotivation` hook.
 *
 * Calls the existing `POST /api/motivation` LLM endpoint to generate a
 * personalized one-line motivational coach insight, mirroring the
 * vanilla pattern at `src/renderer/js/dashboard/motivational-coach.js`.
 *
 * The vanilla flow:
 *   1. Read `athlete_context._daily_motivation` cache.
 *      If `cached.date === today` and `cached.message` exists → use it.
 *   2. Build an athlete summary (~150–200 tokens) covering name,
 *      journey, race history, weekly mileage, today's workout, etc.
 *   3. POST `/api/motivation` with `{ summary }`. The endpoint runs
 *      a daily-rotating "voice" angle prompt against the LLM and
 *      returns `{ message, icon, type }`.
 *   4. Write back to `athlete_context._daily_motivation` so the next
 *      load on the same day is free.
 *
 * React parity: same flow, hook-shaped. Cache hit avoids the LLM
 * round-trip; one fresh fetch per local-date boundary.
 *
 * The motivational line is rendered inside `<ChampionshipCard
 * withMotivation>` via `race.motivationalLine`. The route (today.tsx)
 * composes the output of this hook into the Race object before
 * passing to the card — keeping `useUpcomingRace` a pure DB-shape
 * adapter.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import type { Race } from '../fixtures/race';
import type { TodaySession } from '../fixtures/plan';
import { useAthleteContext } from './useAthleteContext';
import { useAuthFetch } from './useAuthFetch';
import { editGuardianHold } from '../lib/planEdit';
import { weeklyMileageInUnit } from '../lib/dedupePills';
import { resolveDistanceUnit } from '../lib/vdot';

export interface MotivationInsight {
  /** YYYY-MM-DD (local TZ) — the date this insight is valid for. */
  date: string;
  /** The one-line coach message itself. ≤ ~140 chars typically. */
  message: string;
  /** Single emoji from the LLM (default 💪). */
  icon: string;
  /** LLM-tagged category: 'improvement' | 'streak' | 'milestone' | … */
  type: string;
}

export interface UseMotivationArgs {
  /** When false the hook stays idle (returns null). Used to gate on
   *  upstream data being ready (athlete context loaded, today/race
   *  resolved, etc.). */
  enabled: boolean;
  /** Today's session, if loaded. Used to enrich the LLM summary. */
  todaySession: TodaySession | null;
  /** Next race, if loaded. Used to enrich the LLM summary. */
  race: Race | null;
  /**
   * Phase 6 cross-cutting PR 4f — optional proactive-trigger hint
   * from `useProactiveChecks().primaryHint`. When present, appended
   * to the LLM summary as `IMPORTANT CONTEXT: <type> — <message>`
   * so the daily motivation message themes around the trigger (e.g.,
   * if pr_celebration fired today, the message addresses the PR
   * instead of generic content).
   *
   * Pattern mirrors vanilla `motivational-coach.js:152-155`.
   *
   * Note: motivation is cached per-day via `_daily_motivation`. The
   * hint matters only on the day's first LLM call; cache hits on
   * subsequent renders return whatever message the LLM already
   * produced (which may already include the trigger context).
   */
  proactiveHint?: { type: string; message: string } | null;
}

export interface UseMotivationResult {
  data: MotivationInsight | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const EMPTY_RESULT: UseMotivationResult = {
  data: null,
  loading: false,
  error: null,
  refetch: async () => {},
};

/**
 * Local-timezone YYYY-MM-DD. The cache invalidates on the LOCAL day
 * boundary so the message refreshes when the athlete actually wakes up
 * to a new day, not on UTC midnight.
 */
function todayLocalIsoDate(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Build a compact ~150-token athlete summary for the LLM. Mirrors the
 * vanilla `buildAthleteSummary` (motivational-coach.js:62) — same
 * fields, same labels, same journey-mapping table. Trimmed to the
 * data PR 5.1 actually has (no streak / no completion / no RPE — those
 * arrive in Phase 6b once completion logging exists).
 *
 * Pure / exported for tests.
 */
export function buildAthleteSummary(
  ctx: Record<string, unknown>,
  todaySession: TodaySession | null,
  race: Race | null,
  proactiveHint?: { type: string; message: string } | null,
): string {
  const name = pickString(ctx, ['Name', 'name']) || 'Athlete';
  const journey = pickString(ctx, ['_journey']) || 'competitive_solo';
  const age = pickString(ctx, ['Age', 'age']);
  const gender = pickString(ctx, ['Gender', 'gender']);
  const goal = pickString(ctx, ['Goal', 'goal']);
  // B349 — the weekly figure goes to the model in the athlete's unit, with the
  // unit named (a km athlete's stored 36 is "58 km", never a bare 36 the model
  // would call miles). A figure whose unit cannot be stated is left out.
  const unit = resolveDistanceUnit(ctx);
  const storedMileage = pickString(ctx, ['Weekly mileage', 'weekly_mileage']);
  const weeklyMileage = storedMileage ? (weeklyMileageInUnit(storedMileage, unit) ?? '') : '';

  const journeyLabels: Record<string, string> = {
    competitive_team: 'competitive team athlete',
    competitive_solo: 'competitive solo runner',
    time_goal: 'runner with a specific time goal',
    health_fitness: 'health and fitness runner',
    social: 'social runner who runs with friends',
  };

  let summary = `Athlete: ${name}.`;
  summary += ` Journey: ${journeyLabels[journey] || journey}.`;
  if (age) summary += ` ${age}yo`;
  if (gender) summary += ` ${gender}.`;
  if (goal) summary += ` Goal: ${goal}.`;
  if (weeklyMileage) summary += ` Weekly mileage: ${weeklyMileage}.`;
  if (unit === 'km') summary += ' Distance unit: km.';

  if (todaySession) {
    const isRest = (todaySession.intensityLabel || '').toLowerCase() === 'rest';
    if (isRest) {
      summary += ' Today is a rest day.';
    } else {
      // Mirror hero rendering: `{lead} {em}{trail}` — space between
      // lead and em, NO space between em and trail. When em is empty
      // (no extracted distance), emit just `{lead}{trail}`.
      const lead = (todaySession.titleLead || '').trim();
      const em = (todaySession.titleEm || '').trim();
      const trail = (todaySession.titleTrail || '').trim();
      const fullTitle = em ? `${lead} ${em}${trail}` : `${lead}${trail}`;
      summary += ` Today: ${fullTitle}`;
      if (todaySession.subtitle) summary += ` — ${todaySession.subtitle}`;
      if (!summary.endsWith('.')) summary += '.';
    }
  }

  if (race) {
    summary += ` Next race: ${race.name} (${race.distance})`;
    if (race.daysAway === 0) summary += ' — TODAY.';
    else if (race.daysAway === 1) summary += ' — TOMORROW.';
    else summary += ` in ${race.daysAway} days.`;
  }

  // Phase 6 cross-cutting PR 4f — proactive hint cross-module handoff.
  // Mirrors vanilla motivational-coach.js:152-155 — when a proactive
  // trigger fired this load, the LLM gets a directive to address it
  // naturally in the response. Empty/null hint is a no-op.
  if (proactiveHint && proactiveHint.message) {
    const hint = proactiveHint.message.replace(/\.$/, '');
    summary += ` IMPORTANT CONTEXT: ${proactiveHint.type} — ${hint}${/[?!]$/.test(hint) ? '' : '.'} Address this naturally in your message.`;
  }

  return summary;
}

function pickString(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return '';
}

/**
 * Read the cached daily motivation off `athlete_context._daily_motivation`.
 * Returns null when the cache is missing or stale (different local date).
 *
 * Pure / exported for tests.
 */
export function readMotivationCache(
  ctx: Record<string, unknown>,
  now: Date = new Date(),
): MotivationInsight | null {
  const raw = ctx?.['_daily_motivation'];
  if (!raw || typeof raw !== 'object') return null;
  const cached = raw as Partial<MotivationInsight>;
  if (!cached.date || !cached.message) return null;
  if (cached.date !== todayLocalIsoDate(now)) return null;
  return {
    date: cached.date,
    message: cached.message,
    icon: typeof cached.icon === 'string' ? cached.icon : '💪',
    type: typeof cached.type === 'string' ? cached.type : 'general',
  };
}

/**
 * B99 — THE GUARDIAN HOLD IS A HOLD, NOT A FAILURE.
 *
 * `coppaHoldResponse` (api/index.js) answers the B94 gate on `/api/motivation`
 * with 403 `{ error, code: 'guardian_consent_required' }`. Nothing the athlete
 * types or retries clears it, and a success is the only outcome the
 * `_daily_motivation` cache remembers — so the per-instance day gate below
 * (which dies with the hook) sent a held athlete back to the endpoint on every
 * Today mount, spending one 403 and one rate-limit token each time.
 *
 * The hold is therefore remembered BESIDE the hook, keyed by user id and local
 * day: remounts in the same session make no further POST, a new local day asks
 * once more, and a reload asks once more (the memory is module state, never
 * stored). The block itself renders nothing either way — a hold sets no error.
 */
const guardianHoldByUserDay = new Map<string, string>();

export function useMotivation(args: UseMotivationArgs): UseMotivationResult {
  const { enabled, todaySession, race, proactiveHint } = args;
  // B403 — the hint is read by VALUE. The route builds a new `{ type, message }`
  // object on every render, so `refetch` must not depend on the object: it is
  // rebuilt (and the effect re-fires it) only when the type or message changes.
  const hintType = proactiveHint ? proactiveHint.type : null;
  const hintMessage = proactiveHint ? proactiveHint.message : null;
  const fetchAuthed = useAuthFetch();
  const { user, supabase } = useAuthContext();
  const {
    raw: athleteContext,
    loading: athleteLoading,
    mergeContext,
  } = useAthleteContext();

  const [data, setData] = useState<MotivationInsight | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // UX-S2 F4 — one LLM generation in flight per local day. On a cold load the
  // `_daily_motivation` cache isn't written until the first generation
  // completes (LLM latency); meanwhile dep churn (athleteLoading false-flip,
  // proactiveHint arriving, mergeContext identity) re-invokes `refetch`, firing
  // a SECOND `POST /api/motivation` before the cache lands — doubling LLM cost
  // (audit F4). These refs gate generation to once per day regardless of how
  // many times the effect re-fires.
  const inFlightRef = useRef<boolean>(false);
  const lastGenDateRef = useRef<string | null>(null);
  // B403 — one attempt per local day per hint. The catch below reopens the day
  // gate (so a DIFFERENT hint can still try once after a failure); this set is
  // what keeps the SAME day and hint from asking again, however often the host
  // re-renders. Per hook instance, like the refs above: a remount asks once.
  const failedAttemptsRef = useRef<Set<string>>(new Set());

  const refetch = useCallback(async () => {
    if (!enabled || !user || !supabase) {
      setData(null);
      setLoading(false);
      return;
    }
    if (athleteLoading) {
      // Wait for athlete context — building a summary on empty context
      // would force the LLM into the "new athlete" branch incorrectly.
      setLoading(true);
      return;
    }

    // Cache hit?
    const cached = readMotivationCache(athleteContext);
    if (cached) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }

    // F4 — gate to one LLM generation per local day (see refs above).
    const genDate = todayLocalIsoDate();
    // B99 — already held today: stay hidden, stay quiet, spend nothing.
    if (guardianHoldByUserDay.get(user.id) === genDate) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    // B403 — this day and hint already failed: stay as the failure left us (the
    // block hidden, the error kept), set nothing, ask nothing.
    const attemptKey = JSON.stringify([genDate, hintType, hintMessage]);
    if (failedAttemptsRef.current.has(attemptKey)) return;
    if (inFlightRef.current || lastGenDateRef.current === genDate) return;
    inFlightRef.current = true;
    lastGenDateRef.current = genDate;

    setLoading(true);
    setError(null);
    try {
      const hint =
        hintType !== null && hintMessage !== null ? { type: hintType, message: hintMessage } : null;
      const summary = buildAthleteSummary(athleteContext, todaySession, race, hint);
      const res = await fetchAuthed('/api/motivation', {
        method: 'POST',
        body: JSON.stringify({ summary }),
      });
      if (!res.ok) {
        if (res.status === 403) {
          const body = (await res.json().catch(() => null)) as { code?: unknown } | null;
          if (editGuardianHold(body)) {
            // A HOLD: no data, NO ERROR (the catch below would clear the day
            // gate and invite the next mount to ask again), block stays hidden.
            guardianHoldByUserDay.set(user.id, genDate);
            setData(null);
            setError(null);
            return;
          }
        }
        throw new Error(`/api/motivation responded ${res.status}`);
      }
      const json = (await res.json()) as { message?: string; icon?: string; type?: string };
      const message = (json.message ?? '').trim();
      if (!message) {
        // Empty message is treated as a soft failure — don't cache,
        // don't render. The block stays hidden rather than show a
        // half-broken state.
        setData(null);
        setLoading(false);
        return;
      }
      const insight: MotivationInsight = {
        date: todayLocalIsoDate(),
        message,
        icon: typeof json.icon === 'string' && json.icon.length > 0 ? json.icon : '💪',
        type: typeof json.type === 'string' && json.type.length > 0 ? json.type : 'general',
      };
      setData(insight);
      // Persist for the rest of today (mirrors vanilla cache contract).
      mergeContext({ _daily_motivation: insight });
    } catch (err) {
      // Don't synthesize a fallback — the dashboard's contract is
      // "no fake content during empty/error states." The block hides.
      // Clear the per-day gate so a different hint can still try once after a
      // failure (the in-flight guard still prevents dup writes). B403: this
      // day and hint are remembered as failed, so the SAME one never retries.
      lastGenDateRef.current = null;
      failedAttemptsRef.current.add(attemptKey);
      setData(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
    // We intentionally drop `athleteContext` from the dep array to
    // avoid a write-then-read loop: every successful refetch calls
    // `mergeContext`, which mutates `athleteContext`, which would
    // re-invoke refetch. The cache read above guards re-entries when
    // the deps that DO change (todaySession / race / enabled) cause
    // a refire — we'll hit the cache and exit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, user, supabase, athleteLoading, fetchAuthed, todaySession, race, hintType, hintMessage, mergeContext]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  if (!enabled || !user || !supabase) return EMPTY_RESULT;

  return { data, loading, error, refetch };
}

// Exported for unit-testing the pure helpers without a live client.
export const __testing__ = {
  buildAthleteSummary,
  readMotivationCache,
  todayLocalIsoDate,
  /** B99 — clears the module-scope hold memory between tests. */
  resetGuardianHolds: () => guardianHoldByUserDay.clear(),
};
